import React from "react";
import PropTypes from "prop-types";
import { Paper, Typography, LinearProgress, Box } from "@mui/material";
import { Line } from "react-chartjs-2";
import { CHART_COLORS } from "./chartTheme";
import { formatCurrency } from "./formatters";

// Hybrid KPI + trend card for /comparison's single-comparison responses
// (previous_period / previous_year) — growth%, difference, and a day-offset-aligned overlay.
const ComparisonCard = ({ title, current, previous, growthPercent, difference, trend = [], loading }) => {
  const isPositive = growthPercent !== null && growthPercent !== undefined && growthPercent >= 0;
  const growthColor =
    growthPercent === null || growthPercent === undefined
      ? "#6B7280"
      : isPositive
        ? "#5b841b"
        : "#c0392b";

  const trendData = {
    labels: trend.map((point) => `Day ${point.offset + 1}`),
    datasets: [
      {
        label: "Current",
        data: trend.map((point) => point.current),
        borderColor: CHART_COLORS[0],
        backgroundColor: CHART_COLORS[0],
        tension: 0.3,
      },
      {
        label: "Previous",
        data: trend.map((point) => point.previous),
        borderColor: CHART_COLORS[2],
        backgroundColor: CHART_COLORS[2],
        tension: 0.3,
      },
    ],
  };

  return (
    <Paper sx={{ padding: "1rem" }}>
      <Typography sx={{ fontSize: "16px", fontWeight: 700, marginBottom: "1rem" }}>
        {title}
      </Typography>
      {loading && <LinearProgress />}
      {!loading && (
        <>
          <Box sx={{ display: "flex", gap: "2.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>Current</Typography>
              <Typography variant="h5">{formatCurrency(current)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>Previous</Typography>
              <Typography variant="h5">{formatCurrency(previous)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>Growth</Typography>
              <Typography variant="h5" sx={{ color: growthColor }}>
                {growthPercent === null || growthPercent === undefined
                  ? "N/A"
                  : `${growthPercent > 0 ? "+" : ""}${growthPercent}%`}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>Difference</Typography>
              <Typography variant="h5">{formatCurrency(difference)}</Typography>
            </Box>
          </Box>
          {trend.length > 0 && (
            <Box sx={{ height: 280 }}>
              <Line
                data={trendData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => formatCurrency(value),
                      },
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

ComparisonCard.propTypes = {
  title: PropTypes.string.isRequired,
  current: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  previous: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  growthPercent: PropTypes.number,
  difference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trend: PropTypes.array,
  loading: PropTypes.bool,
};

export default ComparisonCard;
